// Accessibility strings
window.accessibilityStrings = {
  imageAvailable: 'Image [index] is now available in gallery view'
};

if (!customElements.get('media-gallery-vertical')) {
  customElements.define(
    'media-gallery-vertical',
    class MediaGalleryVertical extends HTMLElement {
      constructor() {
        super();
        // console.log('MediaGalleryVertical initialized');
        
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        
        // Fallback: if thumbnails not found, try alternative selectors
        if (!this.elements.thumbnails) {
          this.elements.thumbnails = this.querySelector('.thumbnail-slider-vertical');
        }
        if (!this.elements.thumbnails) {
          this.elements.thumbnails = this.querySelector('.product-gallery-vertical__thumbnails');
        }
        
        // Initialize Swiper for main gallery if Swiper is available
        this.initializeMainSwiper();

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // Swiper ignores a swipe that starts on the focused element when it is a video,
        // and a tapped gallery video takes focus. Releasing that focus on a touch lets
        // the shopper swipe past a playing video (mouse and keyboard are unaffected).
        this.elements.viewer.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'mouse') return;
            if (event.target.nodeName === 'VIDEO' && document.activeElement === event.target) event.target.blur();
          },
          true
        );
        // Find all thumbnails and set up click listeners
        const thumbnails = this.querySelectorAll('[data-target]');
        // console.log('Found thumbnails:', thumbnails.length);
        
        thumbnails.forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          const targetId = mediaToSwitch.dataset.target;
          
          if (button) {
            button.addEventListener('click', (e) => {
              e.preventDefault();
              this.setActiveMediaWithTransition(targetId);
            });
          }
        });
        
        // Add navigation button functionality
        this.initializeNavigationButtons();
        
        // Initialize smooth transitions
        this.initializeSmoothTransitions();
        
        // Initialize first image as active
        this.initializeFirstImage();
        
        // Initialize scroll and drag functionality
        this.initializeScrollAndDrag();
      }

      initializeMainSwiper() {
        // console.log('Initializing main swiper');
        // console.log(this.elements.viewer);
        // Check if Swiper is available
             
        const swiperContainer = this.elements.viewer;
        if (!swiperContainer) {
          console.log('Swiper container not found');
          return;
        }

        // Check if Swiper is already initialized
        if (swiperContainer.swiper) {
          // console.log('Swiper already initialized');
          this.mainSwiper = swiperContainer.swiper;
          return;
        }

        // Check if slides are ready
        const slides = swiperContainer.querySelectorAll('.swiper-slide');

        try {
          // Initialize Swiper for main gallery
          const mainSwiper = new Swiper(swiperContainer, {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            allowTouchMove: true,
            grabCursor: true,
            initialSlide: 0, // Always start from the first slide
            scrollbar: {
              el: '.swiper-scrollbar.only-mobile',
              draggable: true,
              snapOnRelease: true,
              dragSize: 50
            },
            on: {
              init: (swiper) => {
                this.mainSwiper = swiper;
                // console.log('Swiper initialized with activeIndex:', swiper.activeIndex);
                // Ensure we start from the first slide
                if (swiper.activeIndex !== 0) {
                  swiper.slideTo(0, 0); // Go to first slide immediately (0 duration)
                }
                // Initialize scrollbar visibility
                this.initializeScrollbarVisibility();
                // Initialize modal-opener conflict resolution
                this.initializeModalOpenerConflictResolution();
                // Ensure first thumbnail is active
                this.ensureFirstThumbnailActive();
              },
              slideChange: (swiper) => {
                // console.log('Main Swiper slide changed to:', swiper.activeIndex);
                // A video that has been swiped (or clicked) away from stops playing
                this.pauseInactiveMedia(swiper.activeIndex);
                // Update thumbnails when main swiper changes
                this.updateThumbnailsFromMainSwiper(swiper.activeIndex);
              },
              touchStart: (swiper, event) => {
                // console.log('Main Swiper touch start', event);
                // Disable modal-opener during swiper interaction
                this.disableModalOpenerDuringSwiper();
              },
              touchMove: (swiper, event) => {
                // console.log('Main Swiper touch move', event);
              },
              touchEnd: (swiper, event) => {
                // console.log('Main Swiper touch end', event);
                // Re-enable modal-opener after swiper interaction
                setTimeout(() => {
                  this.enableModalOpenerAfterSwiper();
                }, 100);
              },
              sliderMove: (swiper, event) => {
                // console.log('Main Swiper slider move', event);
              }
            }
          });

          // console.log('Main Swiper initialization completed');
        } catch (error) {
          console.error('Error initializing main Swiper:', error);
        }
      }

      pauseInactiveMedia(activeIndex) {
        if (!this.mainSwiper) return;
        Array.from(this.mainSwiper.slides).forEach((slide, index) => {
          if (index === activeIndex) return;
          slide.querySelectorAll('video').forEach((video) => {
            if (!video.paused) video.pause();
          });
          slide.querySelectorAll('iframe.js-youtube').forEach((iframe) => {
            iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          });
          slide.querySelectorAll('iframe.js-vimeo').forEach((iframe) => {
            iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
          });
        });
      }

      goToFirstSlide() {
        if (this.mainSwiper) {
          this.mainSwiper.slideTo(0, 0);
        }
      }

      updateThumbnailsFromMainSwiper(activeIndex) {
        if (!this.mainSwiper) return;

        const slides = this.mainSwiper.slides;
        if (!slides || !slides[activeIndex]) return;

        const activeSlide = slides[activeIndex];
        const mediaId = activeSlide.getAttribute('data-media-id');
        
        if (mediaId) {
          console.log('Updating thumbnails from main swiper, mediaId:', mediaId);
          this.setActiveMedia(mediaId, false);
        }
      }

      ensureFirstThumbnailActive() {
        // console.log('Ensuring first thumbnail is active');
        
        // Remove active class from all thumbnails first
        const allThumbnails = this.querySelectorAll('.thumbnail-list__item');
        allThumbnails.forEach(thumbnail => {
          thumbnail.classList.remove('is-active');
        });

        // Add active class to the first thumbnail
        const firstThumbnail = this.querySelector('.thumbnail-list__item');
        if (firstThumbnail) {
          firstThumbnail.classList.add('is-active');
          // console.log('First thumbnail set as active:', firstThumbnail);
        }

        // Ensure the first slide corresponds to the first thumbnail
        if (this.mainSwiper && this.mainSwiper.slides.length > 0) {
          const firstSlide = this.mainSwiper.slides[0];
          const firstSlideMediaId = firstSlide.getAttribute('data-media-id');
          
          // Update thumbnail to match first slide
          if (firstSlideMediaId && firstThumbnail) {
            const targetId = firstThumbnail.getAttribute('data-target');
            if (targetId !== firstSlideMediaId) {
              // console.log('Mismatch between first slide and first thumbnail, syncing...');
              // Find the correct thumbnail for the first slide
              const correctThumbnail = this.querySelector(`[data-target="${firstSlideMediaId}"]`);
              if (correctThumbnail) {
                allThumbnails.forEach(thumb => thumb.classList.remove('is-active'));
                correctThumbnail.classList.add('is-active');
              }
            }
          }
        }
      }

      initializeScrollbarVisibility() {
        if (!this.mainSwiper) return;
        
        const scrollbar = this.querySelector('.swiper-scrollbar.only-mobile');
        if (!scrollbar) return;

        // Show scrollbar only on mobile and when there are multiple slides
        const isMobile = window.innerWidth <= 968;
        const hasMultipleSlides = this.mainSwiper.slides.length > 1;
        
        if (isMobile && hasMultipleSlides) {
          scrollbar.style.display = 'block';
        } else {
          scrollbar.style.display = 'none';
        }

        // Update on window resize
        window.addEventListener('resize', () => {
          const isMobile = window.innerWidth <= 968;
          const hasMultipleSlides = this.mainSwiper.slides.length > 1;
          
          if (isMobile && hasMultipleSlides) {
            scrollbar.style.display = 'block';
          } else {
            scrollbar.style.display = 'none';
          }
        });
      }

      initializeSmoothTransitions() {
        // Swiper handles slide transitions natively
      }

      initializeFirstImage() {
        // Swiper manages slide visibility — just ensure first thumbnail is active
        this.ensureFirstThumbnailActive();
      }

      initializeScrollAndDrag() {
        const thumbnailList = this.querySelector('.thumbnail-list-vertical');
        if (!thumbnailList) return;

        let isDragging = false;
        let startY = 0;
        let scrollTop = 0;

        // Mouse events
        thumbnailList.addEventListener('mousedown', (e) => {
          isDragging = true;
          startY = e.pageY - thumbnailList.offsetTop;
          scrollTop = thumbnailList.scrollTop;
          thumbnailList.style.cursor = 'grabbing';
          e.preventDefault();
        });

        thumbnailList.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          e.preventDefault();
          const y = e.pageY - thumbnailList.offsetTop;
          const walk = (y - startY) * 2;
          thumbnailList.scrollTop = scrollTop - walk;
        });

        thumbnailList.addEventListener('mouseup', () => {
          isDragging = false;
          thumbnailList.style.cursor = 'grab';
        });

        thumbnailList.addEventListener('mouseleave', () => {
          isDragging = false;
          thumbnailList.style.cursor = 'grab';
        });

        // Touch events for mobile
        thumbnailList.addEventListener('touchstart', (e) => {
          isDragging = true;
          startY = e.touches[0].pageY - thumbnailList.offsetTop;
          scrollTop = thumbnailList.scrollTop;
        });

        thumbnailList.addEventListener('touchmove', (e) => {
          if (!isDragging) return;
          e.preventDefault();
          const y = e.touches[0].pageY - thumbnailList.offsetTop;
          const walk = (y - startY) * 2;
          thumbnailList.scrollTop = scrollTop - walk;
        });

        thumbnailList.addEventListener('touchend', () => {
          isDragging = false;
        });

        // Set initial cursor style
        thumbnailList.style.cursor = 'grab';
      }

      initializeNavigationButtons() {
        const prevButton = this.querySelector('.slider-button--prev');
        const nextButton = this.querySelector('.slider-button--next');
        const thumbnailList = this.querySelector('.thumbnail-list-vertical');
        
               
        if (!prevButton || !nextButton || !thumbnailList) {
          console.log('Navigation buttons not found:', { prevButton, nextButton, thumbnailList });
          return;
        }

        const scrollStep = 120; // Height of thumbnail + gap

        // Remove any existing event listeners
        prevButton.replaceWith(prevButton.cloneNode(true));
        nextButton.replaceWith(nextButton.cloneNode(true));
        
        // Get fresh references after cloning
        const newPrevButton = this.querySelector('.slider-button--prev');
        const newNextButton = this.querySelector('.slider-button--next');

        newPrevButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Prev button clicked');
          thumbnailList.scrollBy({
            top: -scrollStep,
            behavior: 'smooth'
          });
        });

        newNextButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Next button clicked');
          thumbnailList.scrollBy({
            top: scrollStep,
            behavior: 'smooth'
          });
        });

        // Show/hide buttons based on scroll position
        const updateButtonVisibility = () => {
          const isAtTop = thumbnailList.scrollTop <= 0;
          const isAtBottom = thumbnailList.scrollTop >= thumbnailList.scrollHeight - thumbnailList.clientHeight;
          
          newPrevButton.style.opacity = isAtTop ? '0.5' : '1';
          newPrevButton.style.pointerEvents = isAtTop ? 'none' : 'auto';
          
          newNextButton.style.opacity = isAtBottom ? '0.5' : '1';
          newNextButton.style.pointerEvents = isAtBottom ? 'none' : 'auto';
        };

        thumbnailList.addEventListener('scroll', updateButtonVisibility);
        
        // Initial button state
        updateButtonVisibility();
      }

      scrollToActiveThumbnail(activeThumbnail) {
        const thumbnailList = this.querySelector('.thumbnail-list-vertical');
        if (!thumbnailList || !activeThumbnail) return;

        const thumbnailRect = activeThumbnail.getBoundingClientRect();
        const containerRect = thumbnailList.getBoundingClientRect();

        // Check if thumbnail is not fully visible
        const isAbove = thumbnailRect.top < containerRect.top;
        const isBelow = thumbnailRect.bottom > containerRect.bottom;

        if (isAbove || isBelow) {
          // Calculate scroll position to center the thumbnail
          const scrollTop = thumbnailList.scrollTop + (thumbnailRect.top - containerRect.top) - (containerRect.height / 2) + (thumbnailRect.height / 2);
          
          thumbnailList.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }

        // Add focus effect
        this.addFocusEffect(activeThumbnail);
      }

      addFocusEffect(thumbnail) {
        // Remove previous focus effects
        this.querySelectorAll('.thumbnail-list__item').forEach(item => {
          item.classList.remove('focus-highlight');
        });

        // Add focus effect to active thumbnail
        thumbnail.classList.add('focus-highlight');

        // Remove focus effect after animation
        setTimeout(() => {
          thumbnail.classList.remove('focus-highlight');
        }, 2000);
      }

      setActiveMediaWithTransition(mediaId) {
        const targetThumbnail = this.querySelector(`[data-target="${mediaId}"]`);
        if (!targetThumbnail) return;

        // Use Swiper to handle slide transition
        if (this.mainSwiper) {
          const slideIndex = Array.from(this.mainSwiper.slides).findIndex(slide =>
            slide.getAttribute('data-media-id') === mediaId
          );
          if (slideIndex !== -1) {
            this.mainSwiper.slideTo(slideIndex);
            // Update thumbnail active state
            this.querySelectorAll('.thumbnail-list__item').forEach(item => {
              item.classList.remove('is-active');
            });
            targetThumbnail.classList.add('is-active');
            this.scrollToActiveThumbnail(targetThumbnail);
            return;
          }
        }

        // Fallback to setActiveMedia if Swiper not available
        this.setActiveMedia(mediaId, true);
      }

      setActiveMedia(mediaId, announceUpdate) {
        // console.log('setActiveMedia called with:', mediaId, 'announceUpdate:', announceUpdate);
        
        // Try to find the media element
        let media = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!media) {
          // Try alternative selectors
          media = this.querySelector(`#Slide-${mediaId}`);
          if (!media) {
            // Try without section prefix
            const mediaIdParts = mediaId.split('-');
            if (mediaIdParts.length > 1) {
              const actualMediaId = mediaIdParts.slice(1).join('-');
              media = this.querySelector(`[data-media-id*="${actualMediaId}"]`);
            }
          }
        }
        
        const thumbnails = this.querySelectorAll('[data-target]');
        const activeThumbnail = this.querySelector(`[data-target="${mediaId}"]`);
        const activeSlide = this.querySelector('[id^="Slide-"].is-active');

        // console.log('Found media element:', media);
        // console.log('Found activeThumbnail:', activeThumbnail);

        if (!media) {
          console.log('Media not found for ID:', mediaId);
          console.log('Available media elements:');
          this.querySelectorAll('[data-media-id]').forEach(el => {
            console.log('  -', el.getAttribute('data-media-id'));
          });
          console.log('Available slide elements:');
          this.querySelectorAll('[id^="Slide-"]').forEach(el => {
            console.log('  -', el.id);
          });
          return;
        }

        // Use Swiper to handle slide transition
        if (this.mainSwiper) {
          const slideIndex = Array.from(this.mainSwiper.slides).findIndex(slide =>
            slide.getAttribute('data-media-id') === mediaId
          );
          if (slideIndex !== -1) {
            this.mainSwiper.slideTo(slideIndex);
          }
        }

        // Update thumbnail active state
        thumbnails.forEach(item => {
          item.classList.remove('is-active');
        });

        if (activeThumbnail) {
          activeThumbnail.classList.add('is-active');
          this.scrollToActiveThumbnail(activeThumbnail);
        }

        if (announceUpdate) this.onSlideChanged();
      }

      onSlideChanged() {
        if (!this.elements.liveRegion) return;
        const activeSlide = this.querySelector('[id^="Slide-"].is-active');
        const activeThumbnail = this.querySelector('[data-target].is-active');

        this.elements.liveRegion.setAttribute('aria-hidden', false);
        const text = this.elements.liveRegion.textContent;
        this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', activeThumbnail.dataset.mediaPosition);
        setTimeout(() => {
          this.elements.liveRegion.textContent = text;
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      }

      initializeModalOpenerConflictResolution() {
        // Find all modal-openers in the gallery
        const modalOpeners = this.querySelectorAll('.product__modal-opener');
        
        modalOpeners.forEach(modalOpener => {
          const mediaToggle = modalOpener.querySelector('.product__media-toggle');
          const mediaIcon = modalOpener.querySelector('.product__media-icon');
          const mediaContainer = modalOpener.querySelector('.product__media');
          
          if (mediaToggle) {
            // Remove the ::after pseudo-element that covers the entire area
            this.removeModalOpenerOverlay(modalOpener);
            
            // Add click handler only to the icon or button, not the entire area
            if (mediaIcon) {
              mediaIcon.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Trigger the modal
                const modalId = modalOpener.getAttribute('data-modal');
                const modal = document.querySelector(modalId);
                if (modal) {
                  modal.show(mediaToggle);
                }
              });
            }

            // Add double-click handler to the media container for opening modal
            if (mediaContainer) {
              let clickTimeout;
              let clickCount = 0;
              let dragDistance = 0;
              let startX, startY;
              
              const handleClick = (e) => {
                // Only handle click if drag distance is minimal (less than 10px)
                if (dragDistance < 10) {
                  clickCount++;
                  
                  if (clickCount === 1) {
                    clickTimeout = setTimeout(() => {
                      clickCount = 0;
                    }, 300);
                  } else if (clickCount === 2) {
                    clearTimeout(clickTimeout);
                    clickCount = 0;
                    
                    // Double click detected - open modal
                    e.preventDefault();
                    e.stopPropagation();
                    const modalId = modalOpener.getAttribute('data-modal');
                    const modal = document.querySelector(modalId);
                    if (modal) {
                      modal.show(mediaToggle);
                    }
                  }
                }
                
                // Reset drag distance
                dragDistance = 0;
              };
              
              mediaContainer.addEventListener('mousedown', (e) => {
                startX = e.clientX;
                startY = e.clientY;
                dragDistance = 0;
              });
              
              mediaContainer.addEventListener('mousemove', (e) => {
                if (startX !== undefined && startY !== undefined) {
                  const deltaX = Math.abs(e.clientX - startX);
                  const deltaY = Math.abs(e.clientY - startY);
                  dragDistance = Math.max(deltaX, deltaY);
                }
              });
              
              mediaContainer.addEventListener('click', handleClick);
              
              // Handle touch events for mobile
              mediaContainer.addEventListener('touchstart', (e) => {
                startX = e.touches[0].clientX;
                startY = e.touches[0].clientY;
                dragDistance = 0;
              });
              
              mediaContainer.addEventListener('touchmove', (e) => {
                if (startX !== undefined && startY !== undefined) {
                  const deltaX = Math.abs(e.touches[0].clientX - startX);
                  const deltaY = Math.abs(e.touches[0].clientY - startY);
                  dragDistance = Math.max(deltaX, deltaY);
                }
              });
              
              mediaContainer.addEventListener('touchend', (e) => {
                // Simulate click for touch devices
                setTimeout(() => {
                  handleClick(e);
                }, 50);
              });
            }
          }
        });
      }

      removeModalOpenerOverlay(modalOpener) {
        // Add CSS to disable the ::after pseudo-element that covers the entire area
        const style = document.createElement('style');
        style.textContent = `
          .product-gallery-vertical .product__modal-opener .product__media-toggle::after {
            display: none !important;
          }
          .product-gallery-vertical .product__modal-opener .product__media-icon {
            cursor: pointer;
            z-index: 10;
            position: relative;
            transition: all 0.2s ease;
          }
          .product-gallery-vertical .product__modal-opener .product__media-icon:hover {
            opacity: 0.8;
            transform: scale(1.1);
          }
          .product-gallery-vertical .product__modal-opener .product__media {
            cursor: grab;
            transition: cursor 0.2s ease;
          }
          .product-gallery-vertical .product__modal-opener .product__media:active {
            cursor: grabbing;
          }
          .product-gallery-vertical .product__modal-opener .product__media:hover {
            cursor: grab;
          }
          .product-gallery-vertical .product__modal-opener .product__media-icon:hover + .product__media {
            cursor: pointer;
          }
        `;
        document.head.appendChild(style);
      }

      disableModalOpenerDuringSwiper() {
        const modalOpeners = this.querySelectorAll('.product__modal-opener');
        modalOpeners.forEach(modalOpener => {
          modalOpener.style.pointerEvents = 'none';
        });
      }

      enableModalOpenerAfterSwiper() {
        const modalOpeners = this.querySelectorAll('.product__modal-opener');
        modalOpeners.forEach(modalOpener => {
          modalOpener.style.pointerEvents = 'auto';
        });
      }

      // Cleanup method
      disconnectedCallback() {
        if (this.mainSwiper && !this.mainSwiper.destroyed) {
          this.mainSwiper.destroy();
        }
      }
    }
  );
}

// Debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
} 